import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, Target, Zap, MessageSquare, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Calendar,
  Mail, Phone, BarChart3, PieChart, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DemoAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/demo-analytics/report?days=${period}`);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error al cargar analíticas');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => `${value || 0}%`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  const overview = data?.overview || {};
  const funnel = data?.lead_funnel || {};
  const revenue = data?.revenue || {};
  const dailyStats = data?.daily_stats || [];
  const businessTypes = data?.by_business_type || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-400" />
            Analíticas de Demo
          </h2>
          <p className="text-gray-400 mt-1">
            Métricas de conversión del sistema de reactivación
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-gray-700 rounded-lg p-1">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  period === days 
                    ? 'bg-emerald-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {days}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Zap className="w-6 h-6" />
            </div>
            <span className="text-emerald-200 text-sm">{period} días</span>
          </div>
          <p className="text-emerald-100 text-sm">Demos Realizadas</p>
          <p className="text-3xl font-bold mt-1">{overview.demos_in_period || 0}</p>
          <p className="text-emerald-200 text-xs mt-2">
            Total histórico: {overview.total_demos_all_time || 0}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-blue-400" />
            </div>
            <span className={`text-sm flex items-center gap-1 ${overview.response_rate > 30 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {overview.response_rate > 30 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(overview.response_rate)}
            </span>
          </div>
          <p className="text-gray-400 text-sm">Tasa de Respuesta</p>
          <p className="text-3xl font-bold text-white mt-1">{overview.demos_with_response || 0}</p>
          <p className="text-gray-500 text-xs mt-2">
            Demos con respuesta del cliente
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-400" />
            </div>
            <span className={`text-sm flex items-center gap-1 ${funnel.conversion_rate > 10 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {funnel.conversion_rate > 10 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(funnel.conversion_rate)}
            </span>
          </div>
          <p className="text-gray-400 text-sm">Leads Convertidos</p>
          <p className="text-3xl font-bold text-white mt-1">{funnel.funnel?.converted || 0}</p>
          <p className="text-gray-500 text-xs mt-2">
            De {funnel.total_leads || 0} leads totales
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-amber-600 to-orange-600 rounded-2xl p-6 text-white"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
          <p className="text-amber-100 text-sm">Valor Generado</p>
          <p className="text-3xl font-bold mt-1">{formatCurrency(revenue.total_generated)}</p>
          <p className="text-amber-200 text-xs mt-2">
            Comisión LG: {formatCurrency(revenue.lokalgrow_commission)}
          </p>
        </motion.div>
      </div>

      {/* Lead Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-emerald-400" />
            Embudo de Conversión
          </h3>
          
          <div className="space-y-3">
            {[
              { key: 'new', label: 'Nuevos', color: 'bg-blue-500', icon: Users },
              { key: 'nurturing', label: 'En Nurturing', color: 'bg-purple-500', icon: Mail },
              { key: 'contacted', label: 'Contactados', color: 'bg-amber-500', icon: Phone },
              { key: 'converted', label: 'Convertidos', color: 'bg-emerald-500', icon: Target },
              { key: 'lost', label: 'Perdidos', color: 'bg-red-500', icon: ArrowDownRight }
            ].map((stage) => {
              const count = funnel.funnel?.[stage.key] || 0;
              const percent = funnel.total_leads > 0 ? (count / funnel.total_leads * 100).toFixed(1) : 0;
              const Icon = stage.icon;
              
              return (
                <div key={stage.key} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${stage.color}/20 flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${stage.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-300">{stage.label}</span>
                      <span className="text-sm font-medium text-white">{count}</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${stage.color} rounded-full transition-all duration-500`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right">{percent}%</span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Demo Metrics */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" />
            Métricas de Demo
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-700/50 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Tasa de Respuesta</p>
              <p className="text-2xl font-bold text-white mt-1">{formatPercent(overview.response_rate)}</p>
            </div>
            <div className="bg-gray-700/50 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Tasa de Completado</p>
              <p className="text-2xl font-bold text-white mt-1">{formatPercent(overview.completion_rate)}</p>
            </div>
            <div className="bg-gray-700/50 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Msgs/Demo Promedio</p>
              <p className="text-2xl font-bold text-white mt-1">{overview.avg_messages_per_demo || 0}</p>
            </div>
            <div className="bg-gray-700/50 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Valor Promedio</p>
              <p className="text-2xl font-bold text-white mt-1">{formatCurrency(funnel.avg_conversion_value)}</p>
            </div>
          </div>

          {/* Mini Chart - Daily Trend */}
          <div className="mt-6">
            <p className="text-sm text-gray-400 mb-2">Tendencia diaria (últimos 14 días)</p>
            <div className="flex items-end gap-1 h-20">
              {dailyStats.slice(-14).map((day, idx) => {
                const maxDemos = Math.max(...dailyStats.slice(-14).map(d => d.demos || 0), 1);
                const height = ((day.demos || 0) / maxDemos) * 100;
                return (
                  <div
                    key={idx}
                    className="flex-1 bg-emerald-500/30 hover:bg-emerald-500/50 rounded-t transition-colors cursor-pointer group relative"
                    style={{ height: `${Math.max(height, 5)}%` }}
                    title={`${day.date}: ${day.demos} demos`}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {day.date?.split('-').slice(1).join('/')}: {day.demos}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Business Type Performance */}
      {businessTypes.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            Rendimiento por Tipo de Negocio
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm">
                  <th className="pb-3 font-medium">Tipo de Negocio</th>
                  <th className="pb-3 font-medium text-right">Leads</th>
                  <th className="pb-3 font-medium text-right">Convertidos</th>
                  <th className="pb-3 font-medium text-right">Tasa Conv.</th>
                  <th className="pb-3 font-medium text-right">Valor Total</th>
                </tr>
              </thead>
              <tbody className="text-white">
                {businessTypes.slice(0, 10).map((bt, idx) => (
                  <tr key={idx} className="border-t border-gray-700">
                    <td className="py-3 capitalize">{bt.business_type || 'Sin definir'}</td>
                    <td className="py-3 text-right">{bt.total_leads}</td>
                    <td className="py-3 text-right">
                      <span className="text-emerald-400">{bt.converted}</span>
                    </td>
                    <td className="py-3 text-right">
                      <span className={bt.conversion_rate > 15 ? 'text-emerald-400' : 'text-amber-400'}>
                        {bt.conversion_rate}%
                      </span>
                    </td>
                    <td className="py-3 text-right font-medium">{formatCurrency(bt.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Revenue Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="bg-gradient-to-r from-emerald-900/50 to-blue-900/50 rounded-2xl p-6 border border-emerald-800/50"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Resumen de Ingresos Generados</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-gray-400 text-sm">Ventas Recuperadas</p>
            <p className="text-3xl font-bold text-white mt-2">{formatCurrency(revenue.total_generated)}</p>
            <p className="text-emerald-400 text-sm mt-1">De clientes inactivos</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-sm">Comisión LokAlGrow (30%)</p>
            <p className="text-3xl font-bold text-emerald-400 mt-2">{formatCurrency(revenue.lokalgrow_commission)}</p>
            <p className="text-gray-500 text-sm mt-1">Nuestro ingreso</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-sm">Beneficio Partners (70%)</p>
            <p className="text-3xl font-bold text-blue-400 mt-2">{formatCurrency(revenue.partner_revenue)}</p>
            <p className="text-gray-500 text-sm mt-1">Ganancia de negocios</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
